import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

export default function ImpressionShare({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher.fetchImpressionShare(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data) return null;

  const primaryColor = brand.colors?.primary || '#2563eb';
  const { impressionShare, lostToBudget, lostToRank } = data;
  const total = impressionShare + lostToBudget + lostToRank;

  // Normalise to 100 if total > 100 (rounding)
  const normFactor = total > 0 ? 100 / total : 1;
  const showPct = Math.round(impressionShare * normFactor);
  const budgetPct = Math.round(lostToBudget * normFactor);
  const rankPct = 100 - showPct - budgetPct;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
              style={{ fontFamily: brand.typography?.headerFont }}>
            Search Impression Share
          </h3>
          <p className="text-xs text-gray-400 mt-1">How often your ads appear when people search</p>
        </div>
        <span className="text-2xl font-bold text-gray-900">{impressionShare}%</span>
      </div>

      {/* Stacked bar */}
      <div className="w-full flex rounded-full h-5 overflow-hidden">
        {showPct > 0 && (
          <div
            className="h-5 transition-all duration-500"
            style={{ width: `${showPct}%`, backgroundColor: primaryColor }}
            title={`Showing: ${impressionShare}%`}
          />
        )}
        {budgetPct > 0 && (
          <div
            className="h-5 bg-amber-400 transition-all duration-500"
            style={{ width: `${budgetPct}%` }}
            title={`Lost to budget: ${lostToBudget}%`}
          />
        )}
        {rankPct > 0 && (
          <div
            className="h-5 bg-gray-300 transition-all duration-500"
            style={{ width: `${rankPct}%` }}
            title={`Lost to rank: ${lostToRank}%`}
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: primaryColor }} />
          <span className="text-xs text-gray-600">Showing ({impressionShare}%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-amber-400" />
          <span className="text-xs text-gray-600">Lost to budget ({lostToBudget}%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-gray-300" />
          <span className="text-xs text-gray-600">Lost to rank ({lostToRank}%)</span>
        </div>
      </div>
    </div>
  );
}
